import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, query, orderBy, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';

// Iconos
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>;

export default function ConfiguracionInventario({ db, showModal }) {
    const [locations, setLocations] = useState([]);
    const [newLocationName, setNewLocationName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [editingLocation, setEditingLocation] = useState(null);

    useEffect(() => {
        setIsLoading(true);
        const q = query(collection(db, 'inventory_locations'), orderBy('name'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setLocations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        }, (error) => {
            console.error("Error al cargar ubicaciones: ", error);
            showModal("Error", "No se pudieron cargar las ubicaciones.");
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [db, showModal]);

    const handleSaveLocation = async (e) => {
        e.preventDefault();
        if (!newLocationName.trim()) {
            showModal("Inválido", "El nombre de la ubicación no puede estar vacío.");
            return;
        }
        try {
            if (editingLocation) {
                const locationDocRef = doc(db, 'inventory_locations', editingLocation.id);
                await updateDoc(locationDocRef, { name: newLocationName.trim() });
                showModal("Éxito", `Ubicación "${newLocationName}" actualizada.`);
            } else {
                await addDoc(collection(db, 'inventory_locations'), { name: newLocationName.trim() });
                showModal("Éxito", `Ubicación "${newLocationName}" creada.`);
            }
            setNewLocationName('');
            setEditingLocation(null);
        } catch (error) {
            console.error("Error al guardar ubicación: ", error);
            showModal("Error", `No se pudo guardar la ubicación: ${error.message}`);
        }
    };

    const handleEditLocation = (location) => {
        setEditingLocation(location);
        setNewLocationName(location.name);
    };

    const handleCancelEdit = () => {
        setEditingLocation(null);
        setNewLocationName('');
    };

    const handleDeleteLocation = async (locationId, locationName) => {
        showModal(
            `Confirmar eliminación`,
            `¿Estás seguro de que quieres eliminar la ubicación "${locationName}"? Esta acción no se puede deshacer.`,
            [
                <button key="confirm" onClick={async () => {
                    try {
                        await deleteDoc(doc(db, 'inventory_locations', locationId));
                        showModal("Éxito", `Ubicación "${locationName}" eliminada.`);
                    } catch (error) {
                        showModal("Error", "No se pudo eliminar la ubicación.");
                    }
                }} className="btn-primary bg-red-600 hover:bg-red-700">Eliminar</button>,
                <button key="cancel" onClick={() => showModal('', '', null)} className="btn-secondary">Cancelar</button>
            ]
        );
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-md space-y-8">
            <div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Gestionar Ubicaciones (Bodegas)</h3>
                <form onSubmit={handleSaveLocation} className="flex items-center gap-4 mb-6">
                    <input type="text" value={newLocationName} onChange={(e) => setNewLocationName(e.target.value)} placeholder="Nombre de la nueva bodega o ubicación" className="input-field flex-grow" />
                    <button type="submit" className="btn-primary whitespace-nowrap">
                        {editingLocation ? 'Actualizar Ubicación' : '+ Añadir Ubicación'}
                    </button>
                    {editingLocation && <button type="button" onClick={handleCancelEdit} className="btn-secondary">Cancelar</button>}
                </form>
                <h4 className="font-semibold text-gray-700 mb-2">Ubicaciones Existentes:</h4>
                {isLoading ? <p>Cargando ubicaciones...</p> : (
                    <ul className="space-y-2">
                        {locations.map(loc => (
                            <li key={loc.id} className="flex justify-between items-center bg-gray-50 p-3 rounded-md">
                                <span className="font-medium text-gray-800">{loc.name}</span>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => handleEditLocation(loc)} className="text-sky-600 hover:text-sky-800 p-1" title="Editar"><EditIcon /></button>
                                    <button onClick={() => handleDeleteLocation(loc.id, loc.name)} className="text-red-500 hover:text-red-700 p-1" title="Eliminar Ubicación">
                                        <TrashIcon />
                                    </button>
                                </div>
                            </li>
                        ))}
                        {locations.length === 0 && <p className="text-gray-500">No hay ubicaciones creadas.</p>}
                    </ul>
                )}
            </div>
            <div className="border-t pt-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Gestionar Roles y Permisos</h3>
                <p className="text-gray-600">La asignación de roles a usuarios se realiza desde el módulo de <span className="font-semibold">Usuarios y Permisos</span>. Allí podrás seleccionar los roles (ej. Administrador, Bodega, Producción) y asignarles una de las ubicaciones creadas aquí.</p>
            </div>
        </div>
    );
};